import { HttpContext } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { MessageFoldersService } from '@modules/messages/services/message-folders.service';
import { MessagesService } from '@modules/messages/services/messages.service';
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { type DropHint, DragNDropService } from '@modules/drag-n-drop/services/drag-n-drop.service';
import { DragData, dragDataTypeAllowed, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import {
  type TransitionCheckableMessage,
  type TransitionResult,
  TRANSITION_PREDICATES,
  evaluateTransitionBatch,
} from '@modules/messages/dnd/mail-transitions';
import { CLEARED_ROUTING_FLAGS } from '@modules/messages/types/message';
import { Message } from '@modules/messages/types/message';
import { MessageFolder } from '@modules/messages/types/message-folder';
import { MessageFoldersFilters } from '@modules/messages/types/message-folders-filters';
import { type VirtualFolder } from '@modules/messages/types/virtual-folder';
import { StateKey } from '@modules/settings/types/state-key';
import { Team } from '@modules/settings/types/team';
import { BaseSidebarContainersTreeComponent } from '@modules/common/components/base-sidebar-containers-tree/base-sidebar-containers-tree.component';
import { MessagesCounts } from '@modules/messages/types/messages-counts';
import { MessageSummaryDto } from '@sharedlib/dtos/dto/mail-app/message.dto';
import { EMPTY, from, type Observable } from 'rxjs';
import { concatMap, last, takeUntil } from 'rxjs/operators';
import { mailboxTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { MailboxTenantScopeService } from '@modules/common/services/mailbox-tenant-scope.service';
import {
  type MailRailFolderTree,
  MailRailFolderTrees,
} from '@modules/messages/services/mail-rail-folder-trees.service';

@Component({
  selector: 'app-side-bar-folders-tree',
  templateUrl: './side-bar-folders-tree.component.html',
  styleUrls: ['./side-bar-folders-tree.component.less'],
  animations: [heightAnimation],
  standalone: false,
})
export class SideBarFoldersTreeComponent
  extends BaseSidebarContainersTreeComponent<MessageFolder, Message, MessageFoldersFilters, VirtualFolder>
  implements OnInit, OnDestroy, MailRailFolderTree
{
  @Input() messagesCount: MessagesCounts;
  @Input() mailboxTenantId: string | null | undefined;

  selfDragDataTypes = [DragDataTypes.message, DragDataTypes.messageFolder];
  containerDragDataType = DragDataTypes.messageFolder;
  application = Application.mail;
  treeStateKey = StateKey.mailSidebarFoldersTree;

  protected isDragging = false;
  private currentDragData: DragData | null = null;
  private readonly dragNDropService = inject(DragNDropService);
  private readonly mailboxTenantScopeService = inject(MailboxTenantScopeService);
  /** Optional: a tree drawn outside Mail's app context is not the rail's. */
  private readonly railFolderTrees = inject(MailRailFolderTrees, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);

  private get draggedMessages(): readonly TransitionCheckableMessage[] {
    return this.currentDragData?.data?.filter(MessageSummaryDto.is) ?? [];
  }

  private hintedFolderId: string | null = null;

  /**
   * Whether folders must render as invalid drop targets for the current drag. Cached per drag
   * (recomputed in the `getDraggingDataChanges()` subscription) because the template reads it on
   * every change detection pass for every folder row.
   */
  protected dropBlocked = false;

  /** Hint shown while hovering a folder with the current drag, or null when the drop is unremarkable. */
  private dropHint: DropHint | null = null;

  /**
   * Whether the batch may be *dropped* on a folder — which is not the same question as whether it
   * may be moved there. Sent emails can't be relocated out of Sent, but they can still be copied or
   * stitched into a folder (see {@link getDndModalButtons}), so they must stay droppable; only the
   * Move button disappears from the drop modal. Droppability is therefore decided by the movable
   * remainder of the batch, and a batch that is entirely Sent is droppable.
   */
  private static evaluateFolderDrop(messages: readonly TransitionCheckableMessage[]): TransitionResult {
    const movable = messages.filter((message) => !TRANSITION_PREDICATES.isSent(message));
    return movable.length === 0 ? { allowed: true, clears: [] } : evaluateTransitionBatch(movable, 'user_folder');
  }

  /** Recomputes the cached drop affordance (blocked state + hint) for the drag that just started/ended. */
  private updateDropAffordance(): void {
    if (this.currentDragData?.type !== DragDataTypes.message) {
      this.dropBlocked = false;
      this.dropHint = null;
      return;
    }

    const messages = this.draggedMessages;
    const result = SideBarFoldersTreeComponent.evaluateFolderDrop(messages);
    const plural = (this.currentDragData?.data?.length ?? 0) > 1;

    this.dropBlocked = !result.allowed;

    if (!result.allowed) {
      const text = (plural && result.hintPlural) || result.hint || "Can't move here";
      this.dropHint = { text, icon: 'sapphire-forbidden', stacked: plural };
      return;
    }

    // Droppable, but Move won't be on offer — say so up front rather than letting the user
    // discover it in the modal.
    if (messages.some(TRANSITION_PREDICATES.isSent)) {
      this.dropHint = {
        text: plural ? 'Sent emails can only be copied to a folder' : 'A Sent email can only be copied to a folder',
        icon: 'sapphire-info',
        stacked: plural,
      };
      return;
    }

    this.dropHint = null;
  }

  protected onItemDragEnter(_dragData: DragData, folderId: string): void {
    this.hintedFolderId = folderId;
    this.dragNDropService.setDropHint(this.dropHint);
  }

  protected onItemDragLeave(folderId: string): void {
    if (this.hintedFolderId === folderId) {
      this.hintedFolderId = null;
      this.dragNDropService.setDropHint(null);
    }
  }

  public getDndModalButtons(dragData: DragData, item: MessageFolder) {
    // Folder-on-folder drags are short-circuited to handleStitch in the base class
    // (containerDragDataType), so this method only runs for message drags.
    //
    // Sent emails (outgoing + state=SENT) cannot be moved out of Sent — that would change a
    // permanent direction/state. We hide the Move button when the batch contains any Sent
    // message so the only meaningful options are Copy and Stitch. The context menu applies the
    // same rule: its Move To dialog drops the folder picker for Sent batches while keeping the
    // Archive/Trash targets, which are still legal destinations for a Sent email.
    // isSent is location-aware (false for archived/trashed/in-folder copies of sent emails),
    // so an archived sent email still gets the full Copy/Move/Stitch dialog.
    const containsSent = dragData.data.filter(MessageSummaryDto.is).some(TRANSITION_PREDICATES.isSent);

    const buttons = [
      { title: 'Cancel', close: true },
      { title: 'Copy', click: () => this.handleDuplicate(dragData, item), close: true },
    ];

    if (!containsSent) {
      buttons.push({ title: 'Move', click: () => this.handleMove(dragData, item), close: true });
    }

    buttons.push({ title: 'Stitch', click: () => this.handleStitch(dragData, item), close: true });

    return buttons;
  }

  /**
   * Drafts and scheduled emails can't be moved, copied, or stitched in the 1st-panel sidebar
   * tree — folders render disabled (via `dropBlocked`) with the "Can't move a Draft to a folder"
   * hint, and a drop that lands anyway snaps back silently. Stitching is still available for
   * these messages via the 2nd/3rd/4th-panel folder lists, where MessageFolderComponent
   * auto-stitches without a modal.
   *
   * Evaluated with the same helper that drives the hint, so what the drag affordance promises and
   * what the drop does can't drift apart — in particular Sent batches, which look droppable and
   * are (into the Copy/Stitch modal).
   */
  public override dndDrop(dragData: DragData, item: MessageFolder): void {
    if (dragData.type === DragDataTypes.message) {
      const messages = dragData.data.filter(MessageSummaryDto.is);
      if (!SideBarFoldersTreeComponent.evaluateFolderDrop(messages).allowed) {
        return;
      }
    }
    super.dndDrop(dragData, item);
  }

  dndPredicate =
    (stitchItem: Stitch) =>
    (dragData: DragData): boolean =>
      stitchItem &&
      !(
        dragData.type === DragDataTypes.messageFolder &&
        dragData.data.length === 1 &&
        dragData.data?.[0]?.id === stitchItem?.id
      ) &&
      dragDataTypeAllowed(dragData.type);

  constructor(
    private foldersService: MessageFoldersService,
    private messagesService: MessagesService,
  ) {
    super(foldersService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.railFolderTrees?.register(this);

    this.dragNDropService
      .getDraggingDataChanges()
      .pipe(takeUntil(this.alive))
      .subscribe((data) => {
        this.isDragging = !!data;
        this.currentDragData = data;
        this.updateDropAffordance();
        if (!NgZone.isInAngularZone()) {
          this.cdr.detectChanges();
        }
      });
  }

  protected override requestContext(): HttpContext | undefined {
    return this.mailboxTenantId === undefined ? undefined : mailboxTenantContext(this.mailboxTenantId);
  }

  /**
   * Opening a folder selects the mailbox that folder lives in, exactly as this tree's own header row
   * already does via `selectFilter`.
   *
   * The rail mounts three of these trees at once, so without this a folder click left the previous
   * section's mailbox selected and the message list read the folder's contents under it: a My
   * Workspace folder opened with Team Workspace selected asked the team for a member folder's
   * messages and rendered "No messages" over a folder that has some (#1030, residual hole iii).
   */
  /**
   * The search popup's "Open folder" (searchopen r4): this tree's own open of that folder, the one its context menu's
   * Open runs — the row selected, which names this tree's mailbox and shows the folder's emails in the list, then
   * {@link openItem} — so it does exactly what a click on the row does.
   */
  public openFolder(folderId: string): boolean {
    const folder = this.items.find((item) => item.id === folderId);
    if (!folder) {
      return false;
    }
    this.handleMenuOpen(folder);
    return true;
  }

  public override ngOnDestroy(): void {
    this.railFolderTrees?.unregister(this);
    super.ngOnDestroy();
  }

  public override openItem(item: MessageFolder): void {
    this.selectOwnMailbox();
    super.openItem(item);
  }

  /**
   * The same write, on the NATIVE click — ahead of the `containersIds` write the selection makes and
   * of the list read that write triggers.
   *
   * `openItem` above was 200 ms late by construction (`appClick`, until instant click on 2026-10-28; it now runs on
   * the same click, in no guaranteed order against the selection's listener), so until #1065 the read went out
   * under the mailbox being left: a clean cross-mailbox folder click measured TEAM at 21 ms, then
   * MEMBER twice at ~620 ms. The 21 ms one is cancelled by the browser, so nothing wrong reaches the
   * view — it is still a dispatch under a tenant this click has already left. Both writes stay: this
   * one wins the race, `openItem`'s is then a no-op on an unchanged value.
   */
  protected override beforeContainerSelectionApplied(): void {
    this.selectOwnMailbox();
  }

  /**
   * Drops a folder selection left behind by another mailbox, but only from the tree that IS the
   * current mailbox — the other mounted tree must not clear a selection it never owned (#1065 ROW 6).
   */
  protected override onItemsReceived(): void {
    // `?? null` for totality, not for a defect: the input is `string | null | undefined` while
    // `effectiveTenantId()` is `string | null`, so an `undefined` host binding would compare false
    // against the personal arm's `null` and skip the drop. Mail binds the input on all three mounts
    // (`side-bar.component.html:13, :89, :171`), so `undefined` has no producer today and reverting
    // this changes nothing observable — it is deliberately un-mutatable (#1065 r2 ROW 4).
    if ((this.mailboxTenantId ?? null) === this.mailboxTenantScopeService.effectiveTenantId()) {
      this.dropForeignContainerSelection();
    }
    // An "Open folder" that expanded this tree's section waits for its folders (searchopen r4).
    this.railFolderTrees?.openPendingIn(this);
  }

  /**
   * Selects the mailbox this tree's rows live in.
   *
   * A tree with no mailbox of its own asks for "the signed-in user's". Whether that is a legal
   * selection is NOT this component's call — inside a team it is not one at all, and
   * `AppTenantScopeService.selectTenant` refuses it there (Pavel, 2026-09-06). One decision, one
   * place, so `selectFilter` and any future caller get it too.
   */
  private selectOwnMailbox(): void {
    this.stateService.setTeam(this.mailboxTenantId ? new Team({ id: this.mailboxTenantId }) : undefined);
  }

  /**
   * Actions
   */

  public handleMove(dragData: DragData, item: MessageFolder): void {
    const message = 'Successfully moved to ' + item.title;

    if (dragData.type === DragDataTypes.message) {
      const ids = this.getIds(dragData.data);
      const messages = dragData.data.filter(MessageSummaryDto.is);
      const result = evaluateTransitionBatch(messages, 'user_folder');

      if (!result.allowed) return;

      const applyMove = (): void => {
        this.messagesService.bunchUpdate(
          { ids },
          { folderId: item.id, ...CLEARED_ROUTING_FLAGS },
          { message, requestContext: this.requestContext() },
        );
      };

      // Target is a manual folder — applyMove already re-sets folderId, so a separate
      // user_folder clear op would be wasteful and has no matching case in getClearOp.
      // Dropping it here keeps the pipe from emitting nothing when it's the only clear.
      const effectiveClears = result.clears.filter((flag) => flag !== 'user_folder');

      if (effectiveClears.length > 0) {
        from(effectiveClears).pipe(
          concatMap((flag) => this.getClearOp(ids, flag) ?? EMPTY),
          last(),
        ).subscribe(() => applyMove());
      } else {
        applyMove();
      }
    } else if (dragData.type === DragDataTypes.messageFolder) {
      this.foldersService.bunchUpdate(
        { ids: this.getIds(dragData.data) },
        { parentId: item.id },
        { message, requestContext: this.requestContext() },
      );
    }
  }

  /** Returns an Observable that clears the given flag/state, or null if not applicable. */
  private getClearOp(ids: string[], flag: string): Observable<unknown> | null {
    switch (flag) {
      case 'deleted':   return this.messagesService.delete({ ids }, false, undefined, { requestContext: this.requestContext() });
      case 'archived':  return this.messagesService.archive({ ids }, false, undefined, { requestContext: this.requestContext() });
      case 'scheduled': return this.messagesService.unscheduleMessages({ ids });
      case 'flagged':   return this.messagesService.flag({ ids }, false, { requestContext: this.requestContext() });
      case 'pinned':    return this.messagesService.pin({ ids }, false, { requestContext: this.requestContext() });
      case 'followed':  return this.messagesService.followUp({ ids }, null, { requestContext: this.requestContext() });
      case 'snoozed':   return this.messagesService.snooze({ ids }, null, { requestContext: this.requestContext() });
      default:          return null;
    }
  }

  public handleDuplicate(dragData: DragData, item: MessageFolder): void {
    if (dragData.type !== DragDataTypes.message) return;

    const ids = this.getIds(dragData.data);
    this.messagesService.duplicate(ids, item.id, {
      message: 'Successfully copied to ' + item.title,
      requestContext: this.requestContext(),
    }).subscribe();
  }
}
